<script lang="ts">
    import vm, { JourneyNode, JourneyStep } from '@journeys/Journeys.viewmodel';
    import { computed, reactive } from 'vue';

    //components
    import RecursiveSegmentList from './RecursiveSegmentList.vue';
    import NodeTemplate from '@journeys/components/NodePartials/NodeTemplate.vue';
    import Notification from '@shared/components/Alerts/Notification.vue';
    // assets
    import { SentIssueOutlineSVG, UserOutlineSVG } from '@shared/assets/vector/ui/IconsUI';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'StepCard',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const { account, editor, activeJourney } = vm;

    const props = defineProps<{
        step: JourneyStep;
    }>();

    const state = reactive({
        showSegmentDefinition: false,
        showDev: false,
    });

    const showChain = (chain: any) => {
        editor.tools.highlight = {};

        for (let link of chain.flat()) editor.tools.highlight[link] = activeJourney.nodes[link];
    };

    const stepListings = computed(() => {
        return {
            issue: account.listings.getListing('/issues/listing', props.step.data.IssueId || ''),
            survey: account.listings.getListing('/surveys/listing', props.step.data.SurveyId || ''),
            segment: account.listings.getListing('/segments', props.step.data.SegmentId),
        };
    });

    const timeSpanSince = computed(() => {
        return props.step.data.SegmentDefinition?.Segments?.find(
            (segment) => segment.TimeSpanSince && segment.IssueId === props.step.data.IssueId
        )?.TimeSpanSince?.split('.')[0];
    });

    const smsTimeSpanSince = computed(() => {
        return props.step.data.SegmentDefinition?.Segments?.find(
            (segment) => segment.TimeSpanSince && segment.SmsMessageId === props.step.data.SmsMessageId
        )?.TimeSpanSince?.split('.')[0];
    });

    (window as any).dev = vm;
</script>

<template>
    <div class="step-container mb-md">
        <div class="d-flex gap-lg align-items-baseline">
            <h2 class="h4 m-0-t">
                {{ $Resources.JourneyStepLabel }}:
                <span role="button" @click="() => showChain(step.chain)" class="link-primary">{{ step.data.Name }}</span>
            </h2>

            <div class="m-auto-l">
                <UserOutlineSVG style="height: 1rem" />
                <span class="m-xs-l">{{ 0 }}</span>
            </div>
            <div>
                <SentIssueOutlineSVG style="height: 1rem" />
                <span class="m-xs-l">{{ 0 }}</span>
            </div>
        </div>

        <div class="fl step-card align-fl-s">
            <div class="step-icon mr-sm mt-xs fl-s">
                <NodeTemplate
                    :id="`icon${step.nodeId}`"
                    :icon="(activeJourney.nodes[step.nodeId] as JourneyNode).i"
                    :iconColor="{ h: 180, s: 100, l: 20 }"
                    :frameColor="{ h: 180, s: 70, l: 60 }"
                    style="transform: scale(1.4)" />
            </div>
            <div class="mt-xs">
                <h3 class="h5 m-0-t d-flex col-gap-xs flex-wrap align-items-baseline" v-if="stepListings.issue">
                    {{ stepListings.issue.Subject || $Resources.NoNamePlaceholder }}
                    <!--{{ stepListings.issue.IssueName ? `(${stepListings.issue.IssueName})` : '' }}-->
                    <a class="btn btn-subtle btn-xs" :href="'/issues/' + stepListings.issue.IssueId" target="_blank" rel="noopener">
                        <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
                        <span class="sr-only">{{ $Resources.PreviewButton }}</span>
                    </a>
                </h3>

                {{ stepListings.survey }}

                <h3 class="h5 m-0-t d-flex col-gap-xs flex-wrap align-items-baseline" v-if="stepListings.survey">
                    {{ stepListings.survey.Title || $Resources.NoNamePlaceholder }}
                    <a class="btn btn-subtle btn-xs" :href="'/surveys/' + stepListings.survey.SurveyId" target="_blank" rel="noopener">
                        <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
                        <span class="sr-only">{{ $Resources.PreviewButton }}</span>
                    </a>
                </h3>

                <a :href="'/Segments/' + step.data.SegmentId" target="_blank" rel="noopener">
                    <h3 class="h5 link-primary m-0-t">{{ $Resources.ViewSegmentLabel }}</h3>
                </a>
            </div>
        </div>

        <div class="d-flex gap-sm m-sm-t">
            <button type="button" class="btn btn-default btn-sm" @click.left="state.showSegmentDefinition = !state.showSegmentDefinition">
                {{ $Resources.SegmentDefinitionLabel }}
            </button>
            <button v-if="editor.dev.show.admin_tools" type="button" class="btn btn-default btn-sm" @click.left="state.showDev = !state.showDev">DEV</button>
        </div>
        <Notification
            v-if=" !step.data.SmsMessageId &&
                step.data.IssueId &&
                step.data.SegmentDefinition?.Segments &&
                step.data.SegmentDefinition.Segments.length > 0 &&
                step.data.SegmentDefinition.Segments.find((segment) => segment.TimeSpanSince && segment.IssueId === step.data.IssueId)
            "
            type="warning"
            class="static mt-sm"
            :message="{
                head: $Resources.NotifyIssueIsResendableLabel,
                body: $Resources.NotifyIssueIsResenableTimeInterval.replace('{{ days }}', timeSpanSince ?? 'error'),
            }"
            sizing="small"
            :inspect="undefined" />
            
        <Notification
            v-if=" step.data.SmsMessageId &&
                !step.data.IssueId &&
                step.data.SegmentDefinition?.Segments &&
                step.data.SegmentDefinition.Segments.length > 0 &&
                step.data.SegmentDefinition.Segments.find((segment) => segment.TimeSpanSince && segment.SmsMessageId === step.data.SmsMessageId)
            "
            type="warning"
            class="static mt-sm"
            :message="{
                head: $Resources.NotifySmsIsResendableLabel,
                body: ($Resources.NotifySmsIsResenableTimeInterval).replace('{{ days }}', smsTimeSpanSince ?? 'error'),
            }"
            sizing="small"
            :inspect="undefined" />

        <RecursiveSegmentList
            class="mt-xxs"
            v-if="state.showSegmentDefinition && step.data.SegmentDefinition.Segments"
            :segments="step.data.SegmentDefinition.Segments"
            :bool="step.data.SegmentDefinition.Bool!"
            :depth="0" />

        <div class="mt-sm" v-if="state.showDev">
            <h3 class="h5 m-0-t">DEV</h3>

            <ul>
                <li>
                    <strong>node/step_id:</strong>
                    {{ step.nodeId }}
                </li>

                <li>
                    <strong>issue_id:</strong>
                    {{ step.data.IssueId || 'NULL' }}
                </li>

                <li>
                    <strong>segment_id:</strong>
                    {{ step.data.SegmentId || 'NULL' }}
                </li>

                <li>
                    <strong>task_id:</strong>
                    {{ step.data.TaskId || 'NULL' }}
                </li>

                <li>
                    <strong>origin_steps:</strong>
                    [{{ step.origins.join(',') || 'NULL' }}]
                </li>
                <li>
                    <strong>Chain:</strong>
                    <ul>
                        <li v-for="(id, idx) in step.chain.flat()" :key="id" @click="() => showChain([id])">
                            - [{{ id }}]
                            {{ (activeJourney.nodes[id] as JourneyNode).data.title }}
                        </li>
                    </ul>
                </li>
            </ul>
        </div>
    </div>
</template>

<style>
    .step-icon {
        width: 6rem;
        height: 6rem;
        overflow: hidden;
        opacity: 1;
        background-color: #faf1fa;
        border-radius: 6px;
    }
</style>
